import { apiBaseUrl, isMockApi, publicApiOrigin } from '@/shared/env'
import { authHeader, getAccessToken } from '@/shared/api/client'
import { compressImage } from '@/shared/media/compressImage'

export type UploadPurpose = 'attendance' | 'disposal' | 'delivery'

export type UploadPhotoOptions = {
  /** Idempotency key absensi — sama dengan event sync (doc §3.2). */
  clientEventId?: string
  /** UUID atau disposal_code batch (wajib untuk disposal). */
  batchId?: string
  deliveryId?: string
  damageReportId?: string
}

export type UploadResult = {
  key: string
  url: string
}

/** Sisipkan access_token sebagai query agar penyajian via <img> tetap terautentikasi. */
function withAccessToken(url: string): string {
  const token = getAccessToken()
  if (!token) return url
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}access_token=${encodeURIComponent(token)}`
}

/**
 * Bangun URL tampilan dari key objek (bucket/key atau legacy).
 */
export function resolvePhotoUrl(keyOrUrl: string | null | undefined): string | null {
  if (!keyOrUrl) return null
  if (
    keyOrUrl.startsWith('http://') ||
    keyOrUrl.startsWith('https://') ||
    keyOrUrl.startsWith('blob:') ||
    keyOrUrl.startsWith('data:')
  ) {
    return keyOrUrl
  }
  if (keyOrUrl.startsWith('/')) {
    return withAccessToken(`${publicApiOrigin}${keyOrUrl}`)
  }
  return withAccessToken(`${apiBaseUrl}/uploads/${keyOrUrl}`)
}

async function toBlob(source: Blob | string): Promise<Blob> {
  if (typeof source !== 'string') return source
  const res = await fetch(source)
  return res.blob()
}

function appendUploadMeta(form: FormData, purpose: UploadPurpose, options?: UploadPhotoOptions) {
  form.append('purpose', purpose)
  if (options?.clientEventId) {
    form.append('client_event_id', options.clientEventId)
  }
  if (options?.batchId) {
    form.append('batch_id', options.batchId)
  }
  if (options?.deliveryId) {
    form.append('delivery_id', options.deliveryId)
  }
  if (options?.damageReportId) {
    form.append('damage_report_id', options.damageReportId)
  }
}

/**
 * Upload foto ke backend (proxy → MinIO). Mengembalikan object key (bucket/path) dan URL tampilan.
 */
export async function uploadPhoto(
  source: Blob | string,
  purpose: UploadPurpose,
  options?: UploadPhotoOptions,
): Promise<UploadResult> {
  if (isMockApi) {
    const url = typeof source === 'string' ? source : URL.createObjectURL(source)
    return { key: url, url }
  }

  const raw = await toBlob(source)
  const blob = await compressImage(raw)
  const form = new FormData()
  form.append('file', blob, `${purpose}.jpg`)
  appendUploadMeta(form, purpose, options)

  const res = await fetch(`${apiBaseUrl}/uploads`, {
    method: 'POST',
    body: form,
    headers: { ...authHeader() },
  })
  if (!res.ok) {
    let message = `Gagal mengunggah foto (${res.status})`
    try {
      const body = (await res.json()) as { error?: string }
      if (body.error) message = body.error
    } catch {
      // keep default
    }
    throw new Error(message)
  }

  const data = (await res.json()) as { key: string; url: string }
  return { key: data.key, url: resolvePhotoUrl(data.key) ?? data.url }
}

/**
 * Minta presigned PUT URL (kontrak doc §6.3). Opsional — multipart uploadPhoto tetap didukung.
 */
export async function presignPhotoUpload(
  purpose: UploadPurpose,
  contentType: string,
  options?: UploadPhotoOptions,
): Promise<{ key: string; uploadUrl: string; method: string; expiresAt: string }> {
  const res = await fetch(`${apiBaseUrl}/uploads/presign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({
      purpose,
      content_type: contentType,
      client_event_id: options?.clientEventId,
      batch_id: options?.batchId,
      delivery_id: options?.deliveryId,
      damage_report_id: options?.damageReportId,
    }),
  })
  if (!res.ok) {
    throw new Error(`Gagal meminta presign (${res.status})`)
  }
  const data = (await res.json()) as {
    key: string
    upload_url: string
    method: string
    expires_at: string
  }
  return {
    key: data.key,
    uploadUrl: data.upload_url,
    method: data.method,
    expiresAt: data.expires_at,
  }
}
